import { Box, Button, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";

const AI_URL = "https://ai.loantabs.com";
const FEATURES_URL = "https://loantabs.com/features/";

const steps = [
  {
    title: "Open the new LoanTabs AI",
    body: "Click the button below. It opens in a new tab, so you can keep these instructions open while you work.",
  },
  {
    title: "Sign up with the same email address you use here",
    body: "This is essential. The import finds your data by matching your email address, and that email must be verified. If you sign up with a different email, nothing will be found.",
  },
  {
    title: "Set up a new business",
    body: "When asked, choose to set up a new business. Do not choose 'Join an existing business'. The import fills your new business with your old data.",
  },
  {
    title: "Go to Admin → Import from Old LoanTabs",
    body: "Only an Admin can open this page. If you are the business owner, that is you.",
  },
  {
    title: "Click 'Preview import'",
    body: "This shows how many borrowers, loans, payments, accounts, team members and other records will come across, plus any notes worth reading. Nothing is written yet.",
  },
  {
    title: "Click 'Confirm import'",
    body: "If the preview looks right, run the real import. A large business can take a few minutes. You can leave the tab open or come back later.",
  },
  {
    title: "Review your data",
    body: "When it finishes you will see a summary of everything migrated and a reconciliation of each account balance. Check your borrowers, loans and accounts before you start using the new system.",
  },
];

const goodToKnow = [
  "The import is a one-time copy, not a sync. Anything you enter here after it completes will not come across, so switch over once you are ready.",
  "Only the original business owner's account can be imported. A staff login will not work.",
  "Team members who had a login here will receive a login for LoanTabs AI, with a temporary password sent by email.",
  "Your existing data here is not changed or deleted by the import.",
  "If an import stops part-way, use 'Retry import' after a few minutes. It cleans up what was written and starts again.",
];

const MigrateToAI = () => (
  <Box
    p="20px"
    display="flex"
    flexDirection="column"
    gap="20px"
    width="100%"
    maxWidth="900px"
  >
    <Typography variant="h3" fontWeight="bold">
      Move to the new LoanTabs AI
    </Typography>
    <Typography variant="h5">
      LoanTabs AI is the new version of LoanTabs. You can bring your borrowers,
      loans, payments, accounts and team members across in a few steps.
    </Typography>

    <Typography variant="body1">
      New in LoanTabs AI: a fresh new interface, an AI assistant that answers
      questions about your portfolio, greater loan configuration, multiple
      branches, loan approval workflows, team roles and permissions, and a wider
      set of reports.{" "}
      <a href={FEATURES_URL} target="_blank" rel="noopener noreferrer">
        See all features
      </a>
    </Typography>

    <Box>
      <Button
        variant="contained"
        color="success"
        size="large"
        endIcon={<OpenInNewIcon />}
        href={AI_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        Go to ai.loantabs.com
      </Button>
    </Box>

    <Typography variant="h4" fontWeight="bold" mt="10px">
      Migration steps
    </Typography>
    <Box
      component="ol"
      sx={{
        m: 0,
        pl: "24px",
        display: "flex",
        flexDirection: "column",
        gap: "14px",
      }}
    >
      {steps.map((step) => (
        <li key={step.title}>
          <Typography variant="h5" fontWeight="600">
            {step.title}
          </Typography>
          <Typography variant="body1">{step.body}</Typography>
        </li>
      ))}
    </Box>

    <Typography variant="h4" fontWeight="bold" mt="10px">
      Good to know
    </Typography>
    <Box
      component="ul"
      sx={{
        m: 0,
        pl: "24px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      {goodToKnow.map((note) => (
        <li key={note}>
          <Typography variant="body1">{note}</Typography>
        </li>
      ))}
    </Box>

    <Typography variant="body1">
      Need help? Write to support@loantabs.com
    </Typography>
  </Box>
);

export default MigrateToAI;
